import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { MessageSquare, ExternalLink, Clock, CheckCircle2 } from "lucide-react";

/** Phase 1 — Legal-leadership tile: "Referrals sent" summary.
 *  Shows totals + last-7-day activity + per-referrer open/responded breakdown.
 *  Rendered inside Dashboard for legal_manager / general_counsel / it_admin. */
export default function ReferralsSummaryTile() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/referrals/summary")
      .then(r => setData(r.data))
      .catch(() => setData(null));
  }, []);
  if (!data) return null;

  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="referrals-summary-tile">
      <div className="px-4 py-2 border-b border-slate-200 flex items-center gap-2 bg-slate-50">
        <MessageSquare className="w-3.5 h-3.5 text-[#005289]" />
        <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Clause Referrals — Sent</div>
        <button onClick={() => nav("/referrals")}
                data-testid="referrals-summary-open-inbox"
                className="ml-auto text-[10px] text-[#005289] hover:underline flex items-center gap-1">
          Inbox <ExternalLink className="w-2.5 h-2.5" />
        </button>
      </div>
      <div className="px-4 py-3 grid grid-cols-4 gap-3">
        <Stat label="Total" value={data.total} color="text-slate-900" testid="referrals-total" />
        <Stat label="Open" value={data.open} color="text-amber-700" testid="referrals-open" />
        <Stat label="Responded" value={data.responded} color="text-emerald-700" testid="referrals-responded" />
        <Stat label="External" value={data.external} color="text-blue-700" testid="referrals-external" />
      </div>
      <div className="px-4 pb-3 flex items-center gap-1 text-[10px] text-slate-500">
        <Clock className="w-2.5 h-2.5" /> {data.last_7d} referral{data.last_7d === 1 ? "" : "s"} in the last 7 days
      </div>
      {data.referrers && data.referrers.length > 0 && (
        <div className="border-t border-slate-100 px-4 py-3">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2">Top referrers</div>
          <div className="space-y-1.5">
            {data.referrers.map(r => {
              const engaged = r.total ? Math.round(r.responded / r.total * 100) : 0;
              return (
                <div key={r.referrer_id || r.referrer_name}
                     data-testid={`referrer-row-${r.referrer_id || r.referrer_name}`}
                     className="flex items-center gap-2 text-xs">
                  <span className="font-semibold text-slate-800 flex-1 truncate">{r.referrer_name}</span>
                  <span className="text-[10px] text-amber-700 font-mono">{r.open} open</span>
                  <span className="text-[10px] text-emerald-700 font-mono flex items-center gap-0.5">
                    <CheckCircle2 className="w-2.5 h-2.5" /> {r.responded}
                  </span>
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${engaged >= 66 ? "bg-emerald-100 text-emerald-800" : engaged >= 33 ? "bg-amber-100 text-amber-800" : "bg-red-100 text-red-800"}`}>
                    {engaged}%
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color, testid }) {
  return (
    <div data-testid={testid}>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
    </div>
  );
}
